import { get } from 'lodash';
import { transformationFunctionWrapper } from 'utility/errors';
import addFileName from '../../../../../../utility/fileName.highcharts';
import EnrolmentCurveChartTransform from './countryDrillDownChart.transform';

const toCumulative = (data, keys) => {
  const totals = keys.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});

  return data.map((item) => {
    const newItem = { ...item };

    keys.forEach((key) => {
      totals[key] += item[key] || 0;
      newItem[key] = totals[key];
    });

    return newItem;
  });
};

const fillMissingMonths = (data, allMonths, defaults) => {
  const dataMap = new Map(data.map((item) => [item.month_id, item]));
  return allMonths.map((month) => {
    if (dataMap.has(month)) {
      return dataMap.get(month);
    }
    return { ...defaults, month_id: month };
  });
};

const transformCurve = (
  activationDataValues,
  dropoutDataValues,
  viewType,
  showEnrollmentMetrics,
  showActivationMetrics,
  hasEnrollmentView,
) => {
  const enrolmentCurveChartTransform = new EnrolmentCurveChartTransform();
  let countryDrillDownCurve = {};
  let enrolmentCurveError = null;

  let processedActivationData = get(
    activationDataValues,
    '[0].actualPlannedData',
    [],
  );
  let processedDropoutData = dropoutDataValues;

  if (viewType === 'cumelative') {
    const allMonthIds = [
      ...new Set([
        ...processedActivationData.map((data) => data.month_id),
        ...processedDropoutData.map((data) => data.month_id),
      ]),
    ].sort((item1, item2) => item1.localeCompare(item2));

    processedActivationData = fillMissingMonths(
      processedActivationData,
      allMonthIds,
      { actual_value: 0, planned_value: 0 },
    );
    processedDropoutData = fillMissingMonths(
      processedDropoutData,
      allMonthIds,
      {
        screening_actual: 0,
        dropout_actual: 0,
        enrolled_actual: 0,
        enrolled_planned: 0,
      },
    );

    processedActivationData.sort(
      (ab, ba) => new Date(ab.month_id) - new Date(ba.month_id),
    );
    processedDropoutData.sort(
      (ab, ba) => new Date(ab.month_id) - new Date(ba.month_id),
    );

    processedActivationData = toCumulative(processedActivationData, [
      'actual_value',
      'planned_value',
    ]);
    processedDropoutData = toCumulative(processedDropoutData, [
      'screening_actual',
      'dropout_actual',
      'enrolled_actual',
      'enrolled_planned',
    ]);
  }

  const aggregateCountryActivationData = processedActivationData.map(
    (data) => ({
      actualSitesActivatedCum: data.actual_value,
      currPlanSitesActivatedCum: data.planned_value,
      origPlanSitesActivatedCum: data.activateToDateValue,
      origPlanSitesActivatedNa: false,
      trialID: 'TAK-861-3001',
      trialMonth: data.month_id,
    }),
  );

  const aggregateScreeningDataValues = processedDropoutData.map((data) => ({
    actualSubjectsScreenedCum: data.screening_actual,
    trialID: 'TAK-861-3001',
    trialMonth: data.month_id,
  }));

  const aggregateDropoutDataValues = processedDropoutData.map((data) => ({
    actualDropoutCum: data.dropout_actual,
    trialID: 'TAK-861-3001',
    trialMonth: data.month_id,
  }));

  const aggregateEnrolledActualDataValues = processedDropoutData.map(
    (data) => ({
      actualEnrolledCum: data.enrolled_actual,
      trialID: 'TAK-861-3001',
      trialMonth: data.month_id,
    }),
  );

  const aggregateEnrolledPlannedDataValues = processedDropoutData.map(
    (data) => ({
      plannedEnrolledCum: data.enrolled_planned,
      trialID: 'TAK-861-3001',
      trialMonth: data.month_id,
    }),
  );

  try {
    enrolmentCurveChartTransform.enrolmentData = null;
    enrolmentCurveChartTransform.siteActivationData =
      aggregateCountryActivationData;
    enrolmentCurveChartTransform.screeningData = aggregateScreeningDataValues;
    enrolmentCurveChartTransform.dropoutData = aggregateDropoutDataValues;
    enrolmentCurveChartTransform.enrolledActualData =
      aggregateEnrolledActualDataValues;
    enrolmentCurveChartTransform.enrolledPlannedData =
      aggregateEnrolledPlannedDataValues;
    enrolmentCurveChartTransform.viewType = viewType;
    enrolmentCurveChartTransform.showEnrollmentMetrics = showEnrollmentMetrics;
    enrolmentCurveChartTransform.showActivationMetrics = showActivationMetrics;
    enrolmentCurveChartTransform.hasEnrollmentView = hasEnrollmentView;
    countryDrillDownCurve = enrolmentCurveChartTransform._generateChart();
    countryDrillDownCurve = addFileName(
      countryDrillDownCurve,
      'countryDrillDownCurve',
    );
  } catch (error) {
    enrolmentCurveError = error;
  }

  if (enrolmentCurveError) throw new Error(enrolmentCurveError);

  return countryDrillDownCurve;
};

const transform = (
  activationData,
  dropoutData,
  viewType,
  showEnrollmentMetrics,
  showActivationMetrics,
  hasEnrollmentView,
) => {
  const activationDataValues = get(activationData, 'data');
  const dropoutDataValues = get(dropoutData, 'data');

  const chartData = transformCurve(
    activationDataValues,
    dropoutDataValues,
    viewType,
    showEnrollmentMetrics,
    showActivationMetrics,
    hasEnrollmentView,
  );

  const returnData = {
    countryDrillDown: {
      chartData,
    },
  };
  return returnData;
};

export default transformationFunctionWrapper(transform);
